import { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { TitleSM } from '@/atoms/Text'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { useUser } from '@/services/UserService'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { TheatricalExpressAllPerkViewer } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressAllPerkViewer'
import { TheatricalExpressCheckoutButton } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressCheckoutButton'
import { TheatricalExpressFrequencySelector } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressFrequencySelector'
import { TheatricalExpressOffersHeader } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressOffersHeader'
import { TheatricalExpressPerkViewer } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPerkViewer'
import { TheatricalExpressPriceOptions } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPriceOptions'
import { TheatricalPifExpressPaymentOptions } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressPaymentOptions'

interface TheatricalPifExpressOffersProps {
  theatricalRelease: TheatricalReleaseResult
  isOneTimeOnly?: boolean
  isTop?: boolean
  topExperimentOn?: boolean
  shouldShowPeopleImpacted?: boolean
  shouldShowDedicationExperiment?: boolean
  earlyAccessMinimumPrice?: number
  isExpressCheckout?: boolean
  handleExpressPifComplete?: () => void
  shouldUseEqualColumns?: boolean
  shouldShowAllPerks?: boolean
  pifNameInCreditsEnabled?: boolean
  newPifLayout?: boolean
}

export const TheatricalPifExpressOffers: FC<TheatricalPifExpressOffersProps> = ({
  theatricalRelease,
  isTop = false,
  topExperimentOn = false,
  isOneTimeOnly = false,
  shouldShowPeopleImpacted = false,
  shouldShowDedicationExperiment = false,
  earlyAccessMinimumPrice,
  isExpressCheckout = false,
  handleExpressPifComplete,
  shouldUseEqualColumns = false,
  shouldShowAllPerks = false,
  pifNameInCreditsEnabled = false,
  newPifLayout = false,
}) => {
  const [isSubscription, setIsSubscription] = useState(false)
  const [shouldOnlyShowCreditCardForm, setShouldOnlyShowCreditCardForm] = useState(false)

  const { offersData, allPerks, selectedOffer, handleSetSelectedOffer, handlePerkClick, handleSetSelectedOfferId } =
    usePayItForwardOfferData()
  const { user } = useUser()
  const { query, isReady } = useSafeRouter()
  const { t } = useTranslate('pif')
  const { trackProductViewed } = useEcommerceEvents()

  useEffect(() => {
    if (isReady && !!query?.defaultOffer) {
      handleSetSelectedOfferId(query?.defaultOffer as string)
    }
  }, [handleSetSelectedOffer, handleSetSelectedOfferId, isReady, query?.defaultOffer])

  const isGuest = !user

  const onComplete = useCallback(async () => {
    if (handleExpressPifComplete) handleExpressPifComplete()
  }, [handleExpressPifComplete])
  const toggleSwitch = (isSub: boolean) => {
    setIsSubscription(isSub)
  }

  if (!offersData) return null

  const shouldHideBottom = !isTop && topExperimentOn

  if (!selectedOffer) return null
  return (
    <InView
      id="pay-it-forward-offers"
      as="section"
      className={classNames('mb-12 w-full rounded-xl border border-core-gray-200 bg-white px-6 py-8 lg:mb-16', {
        'lg:hidden': isTop,
        'hidden lg:block': shouldHideBottom,
      })}
      triggerOnce
      onChange={(inView) => {
        if (!inView) return

        trackProductViewed({
          funnel: 'pif',
          product_id: selectedOffer.id,
          sku: selectedOffer.slug,
          category: 'pif',
          name: selectedOffer.name,
          price: getPriceForCurrency(selectedOffer.price, selectedOffer.currency),
          currency: selectedOffer.currency as MoneyCurrencyCode,
          quantity: 1,
          projectSlug: theatricalRelease?.theatricalSlug,
        })
      }}
    >
      <TheatricalExpressOffersHeader
        shouldShowPeopleImpacted={shouldShowPeopleImpacted}
        projectName={theatricalRelease?.title as string}
        selectedOffer={selectedOffer}
      />
      {!isOneTimeOnly && <TheatricalExpressFrequencySelector onClick={toggleSwitch} isSubscription={isSubscription} />}
      <>
        <TheatricalExpressPriceOptions
          shouldUseEqualColumns={shouldUseEqualColumns}
          shouldShowPeopleImpacted={shouldShowPeopleImpacted}
          offers={offersData}
          onClick={handleSetSelectedOffer}
          selectedOffer={selectedOffer}
          newPifLayout={newPifLayout}
          pifNameInCreditsEnabled={pifNameInCreditsEnabled}
        />
        {shouldShowAllPerks ? (
          <TheatricalExpressAllPerkViewer
            earlyAccessMinimumPrice={earlyAccessMinimumPrice}
            selectedOffer={selectedOffer}
            allPerks={allPerks}
            handlePerkClick={handlePerkClick}
            isExpressCheckout={isExpressCheckout}
            theatricalSlug={theatricalRelease?.theatricalSlug}
            projectName={theatricalRelease?.title as string}
          />
        ) : (
          <TheatricalExpressPerkViewer
            earlyAccessMinimumPrice={earlyAccessMinimumPrice}
            shouldShowDedicationExperiment={shouldShowDedicationExperiment}
            selectedOffer={selectedOffer}
            theatricalSlug={theatricalRelease?.theatricalSlug}
            projectName={theatricalRelease?.title as string}
            isExpressCheckout={isExpressCheckout}
            newPifLayout={newPifLayout}
            pifNameInCreditsEnabled={pifNameInCreditsEnabled}
          />
        )}
        {isExpressCheckout ? (
          <>
            <div className="my-6 h-0.5 w-full rounded-full bg-core-gray-100" />
            {!shouldOnlyShowCreditCardForm && (
              <TitleSM className="pb-4" weight="bold">
                <Translate t={t} i18nKey="expressCheckout">
                  Express Checkout
                </Translate>
              </TitleSM>
            )}
            <TheatricalPifExpressPaymentOptions
              disabled={false}
              key={selectedOffer.id}
              onComplete={onComplete}
              offer={selectedOffer}
              projectSlug={theatricalRelease?.theatricalSlug}
              isShowThanksForSharing={false}
              isGuest={isGuest}
              shouldOnlyShowCreditCardForm={shouldOnlyShowCreditCardForm}
              setShouldOnlyShowCreditCardForm={setShouldOnlyShowCreditCardForm}
            />
          </>
        ) : (
          <TheatricalExpressCheckoutButton
            selectedOffer={selectedOffer}
            projectSlug={theatricalRelease?.theatricalSlug}
            isSubscription={isSubscription}
          />
        )}
      </>
    </InView>
  )
}
